import React, { useState } from 'react';
import { ItemGrid, type ItemData } from '../../../components/ItemKit';
import type { BankActions, BankGoldNotice, BankState, BankUnavailableReason } from '../../../hooks/useBank';
import { TomeButton, TomeChapter } from '../../../primitives/tome';
import type { InventoryItem } from '../../../../../shared/CharacterTypes';
import { BankGoldForm } from './BankGoldForm';
import { BankSatchel, SATCHEL_GAP, SATCHEL_PAD, SATCHEL_SLOT } from './BankSatchel';
import { BankTransferBar, type BankSide } from './BankTransferBar';
import './Bank.css';

/**
 * BankView — the Bank window body, presentational: vault ↔ inventory transfer
 * from props. BankContent (../BankWindow.tsx) wires useBank + useInventory;
 * the offline gallery renders it from fixtures.
 *
 * The Tome page: the TomeWindow frame supplies the parchment and the zone's
 * tokens; each side is a chapter with its purse on top — balance in gilt
 * figures, a typed amount, All, and the action (BankGoldForm) — over a leather
 * satchel, the one dark place, which keeps the dark item slots legible.
 * A tap selects an item for the transfer bar (BankTransferBar); double-click
 * or right-click still moves an entire stack between bag and vault.
 */

export interface BankViewInventory {
  items: InventoryItem[];
  gold: number;
  maxSlots: number;
}

export interface BankViewProps {
  bank: BankState & BankActions;
  inventory: BankViewInventory;
  /** Dev/QA: show the Grant test gear button. */
  showTestGear?: boolean;
}

const UNAVAILABLE_COPY: Record<BankUnavailableReason, string> = {
  'no-server-protocol':
    'Deposits and withdrawals are not available yet. Your items and gold stay in your bag.',
  'no-local-producer':
    'Your vault is unavailable in this session. Deposits and withdrawals remain disabled.',
};

/** As many whole slots as the leaf holds: a window can be resized, and a saved one may be narrower. */
const GRID_STYLE: React.CSSProperties = {
  padding: SATCHEL_PAD, gap: SATCHEL_GAP, gridTemplateColumns: `repeat(auto-fill, ${SATCHEL_SLOT}px)`, justifyContent: 'center',
};

/** Map a gameplay InventoryItem onto the ItemKit display shape. */
const KIT_RARITIES = ['common', 'uncommon', 'rare', 'epic', 'legendary'] as const;
function toItemData(item: InventoryItem): ItemData {
  const rarity =
    item.rarity && (KIT_RARITIES as readonly string[]).includes(item.rarity)
      ? (item.rarity as (typeof KIT_RARITIES)[number])
      : undefined;
  return {
    id: item.id,
    name: item.name,
    icon: item.icon,
    rarity,
    quantity: item.quantity,
    type: item.type,
    description: item.description,
  };
}

function noticeFor(notice: BankGoldNotice | null | undefined, action: BankGoldNotice['action']) {
  return notice && notice.action === action ? notice : null;
}

export const BankView: React.FC<BankViewProps> = ({ bank, inventory, showTestGear = false }) => {
  const {
    bankItems, bankGold, bankMaxSlots, available, unavailableReason, goldNotice, itemNotice = null, itemPending = false,
    deposit, withdraw, depositGold, withdrawGold, grantTestGear,
  } = bank;
  const { items: inventoryItems, gold: inventoryGold, maxSlots: inventoryMaxSlots } = inventory;
  const [selection, setSelection] = useState<{ side: BankSide; id: string } | null>(null);

  const inventoryCapacity = Math.max(inventoryItems.length, inventoryMaxSlots);
  const vaultCapacity = available ? Math.max(bankItems.length, bankMaxSlots) : 0;

  const depositStack = (item: ItemData | null) => {
    if (!item || itemPending) return;
    deposit(item.id, item.quantity ?? 1);
  };

  const withdrawStack = (item: ItemData | null) => {
    if (!item || itemPending) return;
    withdraw(item.id, item.quantity ?? 1);
  };

  const selectOn = (side: BankSide) => (item: ItemData | null) => setSelection(current =>
    !item || (current?.side === side && current.id === item.id) ? null : { side, id: item.id });

  // Read from the live lists, so a stack that shrinks or leaves is followed.
  const selectedItem = available && selection
    ? (selection.side === 'bag' ? inventoryItems : bankItems).find(item => item.id === selection.id) ?? null
    : null;
  const selected = selection && selectedItem ? { side: selection.side, item: selectedItem } : null;
  const selectedIndex = (side: BankSide) =>
    selected?.side === side ? (side === 'bag' ? inventoryItems : bankItems).indexOf(selected.item) : undefined;

  return (
    <div className="bank" data-testid="bank-content" data-bank-available={String(available)}>
      {!available && (
        <div className="bank-notice" role="status" data-testid="bank-unavailable-notice">
          <span className="bank-notice-title">Bank unavailable</span>
          <span>{unavailableReason ? UNAVAILABLE_COPY[unavailableReason] : 'Bank transfers are unavailable in this session.'}</span>
        </div>
      )}
      <div className="bank-columns" aria-disabled={!available}>
        <TomeChapter className="bank-side" title="Inventory"
          count={<span data-testid="bank-inventory-count">{inventoryItems.length} / {inventoryCapacity}</span>}>
          <BankGoldForm direction="deposit" balance={inventoryGold} disabled={!available}
            onSubmit={depositGold} notice={noticeFor(goldNotice, 'depositGold')} />
          <BankSatchel data-testid="bank-inventory-grid">
            <ItemGrid
              items={inventoryItems.map(toItemData)}
              style={GRID_STYLE}
              slotSize="md"
              maxSlots={inventoryCapacity}
              selectedIndex={selectedIndex('bag')}
              onSlotClick={available ? selectOn('bag') : undefined}
              onSlotDoubleClick={available ? depositStack : undefined}
              onSlotRightClick={available ? depositStack : undefined}
            />
          </BankSatchel>
        </TomeChapter>
        <TomeChapter className="bank-side" title="Vault"
          count={available ? <span data-testid="bank-vault-count">{bankItems.length} / {vaultCapacity}</span> : undefined}>
          {available
            ? <BankGoldForm direction="withdraw" balance={bankGold}
              onSubmit={withdrawGold} notice={noticeFor(goldNotice, 'withdrawGold')} />
            : <p className="bank-purse bank-purse-unavailable">Vault balance unavailable</p>}
          <BankSatchel data-testid="bank-vault-grid">
            <ItemGrid
              items={available ? bankItems.map(toItemData) : []}
              style={GRID_STYLE}
              slotSize="md"
              maxSlots={vaultCapacity}
              selectedIndex={selectedIndex('vault')}
              onSlotClick={available ? selectOn('vault') : undefined}
              onSlotDoubleClick={available ? withdrawStack : undefined}
              onSlotRightClick={available ? withdrawStack : undefined}
            />
          </BankSatchel>
        </TomeChapter>
      </div>
      {available && (
        <footer className="bank-foot">
          <BankTransferBar selected={selected} pending={itemPending} notice={itemNotice}
            onTransfer={(side, item, quantity) => (side === 'bag' ? deposit : withdraw)(item.id, quantity)} />
          {showTestGear && (
            <TomeButton tone="quiet" data-testid="bank-test-gear" onClick={grantTestGear}>Grant test gear</TomeButton>
          )}
        </footer>
      )}
    </div>
  );
};
